import React from "react";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import useStudentHome from "../../../../hooks/useStudentHome";
import { StatTile } from "../../../shares/dashboard/DashboardWidgets";
import WelcomeHero from "./WelcomeHero";
import FocusCard from "./FocusCard";
import UpcomingList from "./UpcomingList";
import ProgressPanel from "./ProgressPanel";

/**
 * Student home.
 *
 * Ordered by the question each block answers, rather than by data source:
 *   hero      -> who am I, which day and hour is my class
 *   focus     -> what should I do right now
 *   stats     -> how am I doing, at a glance
 *   deadlines -> what is coming
 *   progress  -> how am I trending
 *
 * All five blocks read from one useStudentHome() call, so the page has a single
 * loading state instead of the three independent ones the old cards each owned.
 * `--i` on each block drives the staggered entrance defined in
 * styles/dashboard/homedash.scss.
 */
function Index() {
    const {
        loading,
        student,
        schedule,
        attendance,
        examAverage,
        assignmentAverage,
        deadlines,
        focus,
    } = useStudentHome();

    const openAssignments = deadlines.filter(
        (item) => item.urgency !== "overdue"
    ).length;

    return (
        <div className="dash-home flex flex-col gap-xl pb-4xl">
            <WelcomeHero
                student={student}
                schedule={schedule}
                loading={loading}
            />

            <div className="grid grid-cols-4 gap-lg max-md:grid-cols-2 max-sm:grid-cols-1">
                <StatTile
                    label="میانگین آزمون"
                    value={examAverage}
                    max={100}
                    suffix="از ۱۰۰"
                    icon={EmojiEventsIcon}
                    tone="primary"
                    index={1}
                />
                <StatTile
                    label="میانگین تکالیف"
                    value={assignmentAverage}
                    max={100}
                    suffix="از ۱۰۰"
                    icon={AssignmentTurnedInIcon}
                    tone="success"
                    index={2}
                />
                <StatTile
                    label="حضور در کلاس"
                    value={attendance?.rate ?? null}
                    max={100}
                    unit="٪"
                    icon={HowToRegIcon}
                    tone={
                        attendance && attendance.rate < 80 ? "warning" : "success"
                    }
                    index={3}
                />
                <StatTile
                    label="تکالیف باز"
                    value={openAssignments}
                    icon={PendingActionsIcon}
                    tone={openAssignments > 0 ? "warning" : "neutral"}
                    index={4}
                />
            </div>

            {/* Focus gets the wider column: it is the page's primary call to
                action, and deadlines/progress are supporting detail. */}
            <div className="grid grid-cols-3 gap-xl max-md:grid-cols-1">
                <div className="col-span-2 flex flex-col gap-xl max-md:col-span-1">
                    <FocusCard focus={focus} loading={loading} index={5} />
                    <UpcomingList
                        deadlines={deadlines}
                        loading={loading}
                        index={6}
                    />
                </div>
                <ProgressPanel
                    examAverage={examAverage}
                    assignmentAverage={assignmentAverage}
                    attendance={attendance}
                    loading={loading}
                    index={7}
                />
            </div>
        </div>
    );
}

export default Index;
